import { Helmet } from 'react-helmet';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { MessageSquare, Search, RefreshCw, User } from 'lucide-react';
import { getChats } from '../../lib/apiService';

const ChatsPage = () => {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const fetchChats = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getChats();
      setChats(data);
    } catch (err) {
      toast.error(err.message || 'Failed to load chats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchChats(); }, [fetchChats]);

  const filtered = chats.filter(c => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      c.user_name?.toLowerCase().includes(q) ||
      c.owner_name?.toLowerCase().includes(q) ||
      c.last_message?.toLowerCase().includes(q) ||
      c.property?.title?.toLowerCase().includes(q)
    );
  });

  const formatTime = (d) => {
    if (!d) return '';
    const date = new Date(d);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays === 0) return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return date.toLocaleDateString('en-IN', { weekday: 'short' });
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  };

  return (
    <div className="p-6 lg:p-8">
      <Helmet>
        <title>Chats | JoldiPabo Sales</title>
      </Helmet>
      <div className="flex flex-col gap-3 mb-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Conversations
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">{chats.length} chat threads between users</p>
        </div>
        <button onClick={fetchChats} className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          className="h-10 pl-9"
          placeholder="Search by name, message, or property..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-8 h-8 border-4 border-[#ee7923] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="flex flex-col items-center justify-center py-16 px-4 bg-white/50 backdrop-blur-sm border-dashed">
          <img src={require('../../assets/paper.png')} alt="Empty" className="w-[150px] h-[150px] object-contain mb-4 opacity-70" />
          <h3 className="text-lg font-semibold text-slate-900 mb-1">No Conversations Found</h3>
          <p className="text-slate-500 text-center max-w-sm mb-6">
            Chats between app users will appear here
          </p>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(chat => (
            <Card
              key={chat.id}
              onClick={() => navigate(`/chats/${chat.id}`)}
              className="p-4 border border-slate-200 cursor-pointer hover:shadow-md hover:border-[#ee7923] transition-all"
            >
              <div className="flex gap-3">
                {/* Avatar */}
                <div className="w-11 h-11 rounded-full bg-[#ee7923] flex items-center justify-center text-white font-bold text-sm shrink-0">
                  {(chat.user_name || 'U')[0].toUpperCase()}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">
                        {chat.user_name || `User #${chat.user_id}`}
                        <span className="text-slate-400 font-normal"> ↔ </span>
                        {chat.owner_name || `User #${chat.owner_id}`}
                      </p>
                      <p className="text-sm text-slate-500 mt-1 truncate">{chat.last_message || 'No messages yet'}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs text-slate-400">{formatTime(chat.last_message_time)}</p>
                      {chat.unread_count > 0 && (
                        <span className="mt-1 inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold bg-[#ee7923] text-white rounded-full">
                          {chat.unread_count > 9 ? '9+' : chat.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChatsPage;
